import React, { useEffect, useState } from "react";
import "./SurveyAnalyticsDashboard.css";
import TabOne from "./TabOne/TabOne";
import Filters from "./Filters";
import TabTwo from "./TabTwo/TabTwo";
import { useQuery, useMutation } from "react-query";
import useAxiosInstance from "../../../../lib/useAxiosInstance";

const SurveyAnalyticsDashboard = ({
  survey,
  children,
  onLocationSelect,
  selectedLocationFromMap,
}) => {
  const axiosInstance = useAxiosInstance();
  const surveyId = survey?._id || survey?.id;
  const [activeTab, setActiveTab] = useState(1);
  /* -------------------- DEFAULT TABS -------------------- */
  const defaultTabs = [
    { id: 1, label: "Tab-1", editing: false },
    { id: 2, label: "Tab-2", editing: false },
    { id: 3, label: "Tab-3", editing: false },
  ];

  const [tabs, setTabs] = useState(defaultTabs);

  /* -------------------- FILTERS -------------------- */
  const [filters, setFilters] = useState({
    block: "",
    village: "",
    booth: "",
  });

  /* -------------------- FETCH TABS (useQuery) -------------------- */
  const { data: tabData } = useQuery(
    ["survey-tabs", surveyId],
    async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/surveyTabs/${surveyId}`
      );
      return res.data?.data;
    },
    {
      enabled: !!surveyId,
      retry: 1,
    }
  );

  /* -------------------- HYDRATE TABS FROM API -------------------- */
  useEffect(() => {
    if (!tabData) return;

    setTabs((prev) =>
      prev.map((t) => {
        if (t.id === 1 && tabData.tab1)
          return { ...t, label: tabData.tab1.label };
        if (t.id === 2 && tabData.tab2)
          return { ...t, label: tabData.tab2.label };
        return t;
      })
    );
  }, [tabData]);
  /* -------------------- SAVE TABS (useMutation) -------------------- */
  const saveTabsMutation = useMutation(async (updatedTabs) => {
    const tab1 = updatedTabs.find((t) => t.id === 1);
    const tab2 = updatedTabs.find((t) => t.id === 2);

    return axiosInstance.post("/api/surveyAnalytics/surveyTabs", {
      surveyId,
      tab1: { id: tab1.id, label: tab1.label },
      tab2: { id: tab2.id, label: tab2.label },
    });
  });

  /* -------------------- TAB EDIT HANDLERS -------------------- */
  const handleDoubleClick = (id) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === id ? { ...t, editing: true } : t))
    );
  };

  const handleChange = (id, value) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === id ? { ...t, label: value } : t))
    );
  };

  const finishEditing = (id) => {
    setTabs((prev) => {
      const updatedTabs = prev.map((t) =>
        t.id === id ? { ...t, editing: false } : t
      );

      // 🔥 Save only when editing finishes
      saveTabsMutation.mutate(updatedTabs);

      return updatedTabs;
    });
  };

  /* -------------------- MAP → FILTER SYNC -------------------- */
  useEffect(() => {
    if (!selectedLocationFromMap) return;

    setFilters((prev) => ({
      ...prev,
      ...(survey?.surveyType === "assembly"
        ? { village: selectedLocationFromMap }
        : { constituency: selectedLocationFromMap }),
    }));
  }, [selectedLocationFromMap, survey?.surveyType]);
  return (
    <div className="dashboard-container">
      {/* 🔹 Editable Tabs */}
      <div className="dashboard-tabs-wrapper">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`dashboard-tab ${
              activeTab === tab.id ? "dashboard-tab-active" : ""
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.editing ? (
              <input
                autoFocus
                value={tab.label}
                className="dashboard-tab-input"
                onChange={(e) => handleChange(tab.id, e.target.value)}
                onBlur={() => finishEditing(tab.id)}
                onKeyDown={(e) =>
                  e.key === "Enter" && finishEditing(tab.id)
                }
              />
            ) : (
              <span
                className="dashboard-tab-label"
                onDoubleClick={() => handleDoubleClick(tab.id)}
              >
                {tab.label}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* 🔹 Map + Content */}
      <div className="dashboard-map">
        <div className="relative w-full h-full">
          {/* Filters */}
          <div className="absolute top-[10%] left-[2%] z-[1000]">
            <Filters
              surveyId={surveyId}
              surveyType={survey?.surveyType}
              selectedLocationFromMap={selectedLocationFromMap}
              onFiltersChange={(f) => {
                setFilters(f);
                const locationName =
                  survey?.surveyType === "assembly"
                    ? f.village
                    : f.constituency;
                onLocationSelect(locationName || null);
              }}
            />
          </div>

          {/* Map */}
          {children}

          {/* Tabs */}
          {activeTab === 1 && <TabOne survey={survey} filters={filters} />}
          {activeTab === 2 && <TabTwo survey={survey} filters={filters} />}
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalyticsDashboard;
